import React, { Fragment, useContext } from 'react';
import type { Dispatch } from 'react';
import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';
import type { CollabEditOptions } from '@atlaskit/editor-common/collab';
import ScreenIcon from '@atlaskit/icon/core/screen';
import { IconButton } from '@atlaskit/button/new';

import { FlagsStateContainer } from '@confluence/flags';
import { fg } from '@confluence/feature-gating';
import { EditContentButton } from '@confluence/edit-button';
import { FavouriteButton } from '@confluence/favourite-button';
import { PageSegmentLoadStart } from '@confluence/browser-metrics';
import { WatchDialog } from '@confluence/watch-dialog';
import { WATCH_BUTTON_METRIC } from '@confluence/watch-dialog/entry-points/pageSegmentLoadMetrics';
import { MANAGE_WATCHER_TAB_TYPES } from '@confluence/manage-watcher-dialog';
import { PageRestrictionsContextProvider } from '@confluence/page-restrictions-context';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { RestrictionsDialogTriggerWrapper } from '@confluence/restrictions';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { CreatePageButtonContextProvider, QuickSummaryPopup } from '@confluence/quick-summary';
import { ContentOwnershipContextProvider } from '@confluence/content-ownership';
import type { ShareContentType } from '@confluence/share';
import {
	type ContentType,
	type ContentMode,
	useIsStarringEnabled,
	useIsWatchDialogEnabled,
	VALID_OPERATION_TARGETS,
} from '@confluence/content-types-utils';
import { useIsClassificationEnabledForContentTypeExpansion } from '@confluence/data-classification/entry-points/hooks';
import { isExternalShareRequest } from '@confluence/external-share-utils';
import { LivePagesModeChanger } from '@confluence/live-pages-features/entry-points/LivePagesModeChanger';
import { useIsMobileApp } from '@confluence/mobile-app-detection';
import { useUnifiedShareDialogEligible } from '@confluence/experiment-unified-share-dialog';
// eslint-disable-next-line import/no-extraneous-dependencies
import { PublishButtonWithMoreOptions } from '@confluence/publish-modal';
import { PreviewPanelTestButton } from '@confluence/preview-panel-api';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import type { WhiteboardAIAgentServiceInterface } from '@confluence/whiteboard-types/entry-points/WhiteboardAIAgentService';
import { SPAViewContext } from '@confluence/spa-view-context';
import { AgentPresenceLoader } from '@confluence/team-presence/entry-points/AgentPresenceLoader';

import { useEditorObjectHeaderContext } from './EditorObjectHeaderContext';
import { ContentStatusComponent } from './content-types-actions/ContentStatus';
import { CollaboratorsComponent } from './content-types-actions/Collaborators';
import { MainActionsComponent } from './content-types-actions/MainActions';
import { MoreActionsComponent } from './content-types-actions/MoreActions';
import { WhiteboardTeammateButton } from './content-types-actions/whiteboards/WhiteboardTeammateButton';
import { ContentTypesWatchDialogButtonLoader } from './ContentTypesWatchDialogButton';
import type {
	ContentActions,
	HeaderItems,
	ContentTypesHeaderQueryNode,
} from './ContentTypesHeader';
import type { ContentTypesHeaderQuery_user } from './__types__/ContentTypesHeaderQuery';
import type { HeaderFeaturesType } from './ContentTypesHeaderFeatures';
import { Presence } from './object-header-items/Presence';
import { EditorCloseButton } from './object-header-items/EditorCloseButton';
import { UnpublishedChangesOrLastEdited } from './object-header-items/UnpublishedChangesOrLastEdited';
import { useDisableEditorActions } from './useDisableEditorActions';
import { useObjectHeaderVersion } from './useObjectHeaderVersion';

export type HeaderItemData = {
	contentId: string;
	contentType: ShareContentType;
	contentMode: ContentMode | undefined;
	contentActions?: ContentActions;
	handleRegisterOnShowCollaboratorSpotlight?: (callback: () => void) => void;
	handleRegisterOnShowHeaderSpotlight?: (callback: () => void) => void;
	allowedFeatures: HeaderFeaturesType;
	contentData: ContentTypesHeaderQueryNode | null | undefined;
	renderCopyLink: () => React.JSX.Element | undefined;
	renderShare: () => React.JSX.Element;
	showVersionHistorySpotlight?: boolean;
	setShowVersionHistorySpotlight?: (value: boolean) => void;
	collabEditOption?: CollabEditOptions;
	isSpaceOverviewPage: boolean;
	readOnly?: boolean;
	isConvertToFolderHighlighted?: boolean;
	setIsConvertToFolderHighlighted?: Dispatch<React.SetStateAction<boolean>>;
	whiteboardAIAgentService?: WhiteboardAIAgentServiceInterface;
	isVersionHistoryPage?: boolean;
	userData: ContentTypesHeaderQuery_user | null | undefined;
};

export const useHeaderItems = ({
	contentId,
	contentType,
	contentMode,
	contentActions,
	handleRegisterOnShowCollaboratorSpotlight,
	handleRegisterOnShowHeaderSpotlight,
	allowedFeatures,
	contentData,
	renderCopyLink,
	renderShare,
	showVersionHistorySpotlight,
	setShowVersionHistorySpotlight,
	collabEditOption,
	isSpaceOverviewPage,
	readOnly,
	isConvertToFolderHighlighted = false,
	setIsConvertToFolderHighlighted,
	whiteboardAIAgentService,
	isVersionHistoryPage,
	userData,
}: HeaderItemData): HeaderItems[] => {
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	const isWatchDialogEnabledForContentType = useIsWatchDialogEnabled(contentType);
	const isStarringEnabledForContentType = useIsStarringEnabled(contentType, contentMode);
	const isExternal = isExternalShareRequest();
	const { isUSDExperimentEnabled } = useUnifiedShareDialogEligible();
	const { isAnonymous } = useContext(SPAViewContext);

	const {
		isPublishingContent,
		hasEditPermission,
		onClickPublish,
		editorTitle,
		saveDraft,
		isUnpublishedDraft,
		shouldRenderPreview,
		onPreviewClicked,
		retrieveEditorTitleAndContent,
	} = useEditorObjectHeaderContext();

	const {
		shouldDisableEditorButtons,
		isPublishDisabledForPublishModal,
		isPublishDisabled,
		pageIsExternallyShared,
	} = useDisableEditorActions(contentId, contentMode);

	const isClassicEdit =
		(contentType === 'page' || contentType === 'blogpost') &&
		(contentMode === 'draft' || contentMode === 'edit');

	const isCompanyHub = isCompanyHubSpaceKey(contentData?.space?.key);

	const isClassificationEnabled = useIsClassificationEnabledForContentTypeExpansion(
		contentData?.type ?? '',
	);
	const isArchived = contentData?.status === 'archived';

	const version = useObjectHeaderVersion({ contentData, useDraftVersion: isClassicEdit });
	const currentUser = {
		fullName: userData?.displayName || '',
	};

	const { isMobileApp } = useIsMobileApp();

	const computedReadOnly =
		readOnly ||
		isExternal ||
		!contentData?.operations?.some(
			(operation) =>
				operation?.targetType &&
				VALID_OPERATION_TARGETS.includes(operation?.targetType as ContentType) &&
				operation?.operation === 'update',
		);

	const isClassificationAllowedInMoreActions = !computedReadOnly && isClassificationEnabled;
	const moreActionsAllowedFeatures = isClassificationAllowedInMoreActions
		? { ...allowedFeatures, showClassificationMenuAction: true }
		: allowedFeatures;

	const hasArchivePermission = contentData?.operations?.some((operation) => {
		return operation?.targetType === contentType && operation?.operation === 'archive';
	});

	const isWhiteboardDigitalTeammateEnabled =
		contentType === 'whiteboard' &&
		isRovoEnabledForContent &&
		fg('cc-ai-whiteboards-enable-digital-teammate');

	const isWhiteboardSummaryEnabled =
		contentType === 'whiteboard' && isAIEnabledForContent && !isWhiteboardDigitalTeammateEnabled;

	const whiteboardSummaryButtonStyles = fg('cc_smarts_white_summarize_button')
		? { border: `${token('border.width')} solid ${token('color.border')}` }
		: { standardButton: true };

	const renderLastEditedForDatabase =
		contentType === 'database' && fg('confluence_frontend_databases_content_wrapper');

	const isPreviewPanelTestEnabled =
		StatsigConfigurations.getString('confluence_page_preview_panel_testing', '') !== '';

	if (isVersionHistoryPage && fg('confluence_frontend_object_header')) {
		// don't show any header items or overflow items for version history page as they are irrelevant
		return [];
	}

	return fg('confluence_frontend_object_header')
		? [
				{
					name: 'lastEdited',
					shouldShowSeparator: false,
					shouldRender:
						renderLastEditedForDatabase ||
						(contentType !== 'embed' && contentType !== 'database' && !isSpaceOverviewPage),
					children: (
						<ErrorBoundary attribution={Attribution.DISCO}>
							<UnpublishedChangesOrLastEdited
								contentId={contentId}
								version={version}
								contentType={contentType}
								contentMode={contentMode}
								spaceKey={contentData?.space?.key ?? ''}
								currentUser={currentUser}
							/>
						</ErrorBoundary>
					),
				},
				{
					name: 'aiAgentPresence',
					shouldShowSeparator: false,
					shouldRender:
						['page', 'blogpost'].includes(contentType) &&
						(contentMode === 'live' || contentMode === 'edit' || contentMode === 'draft') &&
						fg('confluence_team_presence_ai_agents'),
					children: <AgentPresenceLoader />,
				},
				{
					// Can remove the "collaborators" item below once confluence_team_presence_whiteboard is fully rolled out
					name: 'presence',
					shouldShowSeparator: fg('company_hub_creation_ui') && isCompanyHub,
					shouldRender:
						(['page', 'blogpost'].includes(contentType) && contentMode !== 'archived') ||
						(contentType === 'whiteboard' &&
							!isMobileApp &&
							fg('confluence_team_presence_whiteboard')),
					children: (
						<Presence
							collaborators={contentActions?.collaborators}
							contentId={contentId}
							contentType={contentType}
							contentMode={contentMode}
							collabEditOption={collabEditOption}
						/>
					),
				},
				{
					// deprecated in favor of Presence
					name: 'collaborators',
					shouldShowSeparator: false,
					shouldRender:
						!!contentActions?.collaborators &&
						contentType === 'whiteboard' &&
						!isMobileApp &&
						!fg('confluence_team_presence_whiteboard'),
					children: (
						<ErrorBoundary attribution={Attribution.CONTENT_TYPES}>
							<CollaboratorsComponent
								collaboratorData={contentActions?.collaborators!}
								handleRegisterOnShowCollaboratorSpotlight={
									handleRegisterOnShowCollaboratorSpotlight
								}
							/>
						</ErrorBoundary>
					),
				},
				{
					name: 'preview',
					shouldShowSeparator: false,
					shouldRender: fg('company_hub_creation_ui') && isCompanyHub,
					children: (
						<ErrorBoundary attribution={Attribution.COMPANY_HUB}>
							<IconButton
								icon={ScreenIcon}
								appearance="subtle"
								label=""
								onClick={onPreviewClicked}
							/>
						</ErrorBoundary>
					),
				},
				{
					name: 'mainActions', // this only handles showing external link for embed object header
					shouldShowSeparator: false,
					shouldRender:
						!!contentActions?.mainActions &&
						!!allowedFeatures.showMainActions &&
						fg('open_in_new_tab_button_update'),
					children:
						!!contentActions?.mainActions && !!allowedFeatures.showMainActions ? (
							<ErrorBoundary attribution={Attribution.CONTENT_TYPES}>
								<MainActionsComponent mainActions={contentActions.mainActions.mainActions} />
							</ErrorBoundary>
						) : null,
				},
				{
					name: 'summary', // this only handles showing the summary button in whiteboards
					shouldShowSeparator: true,
					shouldRender: isWhiteboardSummaryEnabled,
					children: (
						<ErrorBoundary attribution={Attribution.CONTENT_TYPES}>
							<CreatePageButtonContextProvider
								contentId={contentId}
								spaceKey={contentData?.space?.key}
								contentTitle={contentData?.title}
							>
								<QuickSummaryPopup
									contentId={contentId}
									spaceKey={contentData?.space?.key || undefined}
									contentType={contentType}
									source={contentType}
									buttonStyles={{ border: 'none' }}
									contentPath={window.location.pathname}
								/>
							</CreatePageButtonContextProvider>
						</ErrorBoundary>
					),
				},
				{
					name: 'whiteboard-teammate',
					shouldShowSeparator: true,
					shouldRender: isWhiteboardDigitalTeammateEnabled,
					children: (
						<ErrorBoundary attribution={Attribution.CONTENT_TYPES}>
							<WhiteboardTeammateButton whiteboardAIAgentService={whiteboardAIAgentService} />
						</ErrorBoundary>
					),
				},
				{
					name: 'editIcon',
					shouldShowSeparator: false,
					shouldRender:
						(contentType === 'page' || contentType === 'blogpost') && contentMode === 'view',
					children: (
						<ErrorBoundary attribution={Attribution.PAGE_MODES}>
							<EditContentButton contentId={contentId} />
						</ErrorBoundary>
					),
				},
				{
					name: 'previewPanelTest',
					shouldShowSeparator: false,
					shouldRender: isPreviewPanelTestEnabled,
					children: <PreviewPanelTestButton />,
				},
				{
					name: 'viewEditToggle',
					shouldShowSeparator: false,
					shouldRender:
						Boolean(contentMode === 'live' && hasEditPermission) &&
						!fg('cc_live_pages_toggle_move'),
					children: (
						<ErrorBoundary attribution={Attribution.PAGE_MODES}>
							<Subscribe to={[FlagsStateContainer]}>
								{(flags: FlagsStateContainer) => (
									<LivePagesModeChanger
										contentId={contentId}
										isDisabled={!!shouldDisableEditorButtons}
										flags={flags}
										analyticsSource="livePageScreen"
									/>
								)}
							</Subscribe>
						</ErrorBoundary>
					),
				},
				{
					name: 'update',
					shouldShowSeparator: false,
					shouldRender: isClassicEdit,
					children: (
						<ErrorBoundary attribution={Attribution.PAGE_MODES}>
							<Subscribe to={[FlagsStateContainer]}>
								{(flags: FlagsStateContainer) => (
									<PublishButtonWithMoreOptions
										onClickPublish={onClickPublish}
										publishButtonDisabled={isPublishDisabledForPublishModal ?? isPublishDisabled}
										title={editorTitle}
										onClickPreview={onPreviewClicked}
										userViewingPreview={shouldRenderPreview}
										retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
										isUnpublishedDraft={isUnpublishedDraft}
										flags={flags}
										saveDraft={saveDraft}
										contentType={contentType}
										savingInProgress={isPublishingContent}
										shouldShowWorldIcon={
											pageIsExternallyShared &&
											!(
												fg('confluence_frontend_unified_restrict_and_share') ||
												isUSDExperimentEnabled
											)
										}
										shouldShowFooterButtons
									/>
								)}
							</Subscribe>
						</ErrorBoundary>
					),
				},
				{
					name: 'close',
					shouldShowSeparator: false,
					shouldRender: contentMode === 'draft' || contentMode === 'edit',
					children: (
						<ErrorBoundary attribution={Attribution.PAGE_MODES}>
							<EditorCloseButton
								contentType={contentType}
								contentId={contentId}
								areMainButtonsDisabled={shouldDisableEditorButtons}
								isUnpublishedDraft={contentMode === 'draft'}
							/>
						</ErrorBoundary>
					),
				},
				{
					name: 'share',
					shouldShowSeparator: false,
					shouldRender: !isAnonymous && !isArchived && !!allowedFeatures.showShare && !isCompanyHub,
					children: renderShare(),
				},
				{
					name: 'contentRestrictions',
					shouldShowSeparator: false,
					shouldRender: !isAnonymous && !!allowedFeatures.showContentRestrictions && isArchived,
					children: (
						<PageRestrictionsContextProvider contentId={contentId}>
							<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
								<RestrictionsDialogTriggerWrapper
									contentId={contentId}
									key="restrictions-dialog-trigger"
									triggeredFrom="fromContentType"
								/>
							</ErrorBoundary>
						</PageRestrictionsContextProvider>
					),
				},
				{
					name: 'copy',
					shouldShowSeparator: isWhiteboardSummaryEnabled,
					shouldRender:
						fg('confluence_frontend_unified_restrict_and_share') || isUSDExperimentEnabled
							? isArchived
							: allowedFeatures.showCopy,
					children: renderCopyLink(),
				},
				{
					name: 'moreActions',
					shouldShowSeparator: false,
					shouldRenderInContainer: false,
					shouldRender: !!allowedFeatures.showMoreActions,
					children: (
						<ErrorBoundary attribution={Attribution.DISCO}>
							<ContentOwnershipContextProvider>
								<Subscribe to={[FlagsStateContainer]}>
									{(flags: FlagsStateContainer) => (
										<MoreActionsComponent
											moreActions={contentActions?.moreActions}
											contentId={contentId}
											contentType={contentType}
											contentMode={contentMode}
											spaceKey={contentData?.space?.key ?? ''}
											allowedFeatures={moreActionsAllowedFeatures}
											hasArchivePermission={hasArchivePermission}
											handleRegisterOnShowHeaderSpotlight={handleRegisterOnShowHeaderSpotlight}
											setShowVersionHistorySpotlight={setShowVersionHistorySpotlight}
											showVersionHistorySpotlight={showVersionHistorySpotlight}
											flags={flags}
											contentData={contentData}
											readOnly={computedReadOnly}
											isConvertToFolderHighlighted={isConvertToFolderHighlighted}
											setIsConvertToFolderHighlighted={setIsConvertToFolderHighlighted}
										/>
									)}
								</Subscribe>
							</ContentOwnershipContextProvider>
						</ErrorBoundary>
					),
				},
			]
		: [
				{
					name: 'status',
					shouldShowSeparator: false,
					shouldRender: !!contentActions?.contentStatus,
					children: <ContentStatusComponent contentStatus={contentActions?.contentStatus!} />,
				},
				{
					name: 'collaborators',
					shouldShowSeparator: true,
					shouldRender:
						!!contentActions?.collaborators &&
						contentType !== 'database' &&
						!(contentType === 'whiteboard' && isMobileApp),
					children: (
						<CollaboratorsComponent
							collaboratorData={contentActions?.collaborators!}
							handleRegisterOnShowCollaboratorSpotlight={handleRegisterOnShowCollaboratorSpotlight}
						/>
					),
				},
				{
					name: 'mainActions',
					shouldShowSeparator: false,
					shouldRender:
						!!contentActions?.mainActions &&
						!!allowedFeatures.showMainActions &&
						fg('open_in_new_tab_button_update'), // For FG cleanup, remove this line and remove the second 'mainActions' item.
					children:
						!!contentActions?.mainActions && !!allowedFeatures.showMainActions ? (
							<MainActionsComponent mainActions={contentActions.mainActions.mainActions} />
						) : null,
				},
				{
					name: 'favourite',
					shouldShowSeparator: !isWatchDialogEnabledForContentType,
					shouldRender: isStarringEnabledForContentType && !!allowedFeatures.showFavourite,
					children: <FavouriteButton contentId={contentId} />,
				},
				{
					name: 'watchDialog',
					shouldShowSeparator: true,
					shouldRender: isWatchDialogEnabledForContentType && !!allowedFeatures.showWatchDialog,
					children: (
						<Fragment>
							<PageSegmentLoadStart key={contentId} metric={WATCH_BUTTON_METRIC} />
							<WatchDialog
								contentId={contentId}
								defaultTab={MANAGE_WATCHER_TAB_TYPES.PAGE}
								customTrigger={ContentTypesWatchDialogButtonLoader}
								useShortcut
							/>
						</Fragment>
					),
				},
				{
					name: 'mainActions',
					shouldShowSeparator: false,
					shouldRender:
						!!contentActions?.mainActions &&
						!!allowedFeatures.showMainActions &&
						!fg('open_in_new_tab_button_update'),
					children:
						!!contentActions?.mainActions && !!allowedFeatures.showMainActions ? (
							<MainActionsComponent mainActions={contentActions.mainActions.mainActions} />
						) : null,
				},
				{
					name: 'contentRestrictions',
					shouldShowSeparator: isArchived,
					shouldRender:
						!!allowedFeatures.showContentRestrictions &&
						!(fg('confluence_frontend_unified_restrict_and_share') || isUSDExperimentEnabled),
					children: (
						<PageRestrictionsContextProvider contentId={contentId}>
							<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
								<RestrictionsDialogTriggerWrapper
									contentId={contentId}
									key="restrictions-dialog-trigger"
									triggeredFrom="fromContentType"
								/>
							</ErrorBoundary>
						</PageRestrictionsContextProvider>
					),
				},
				{
					name: 'copy',
					shouldShowSeparator: isWhiteboardSummaryEnabled,
					shouldRender:
						fg('confluence_frontend_unified_restrict_and_share') || isUSDExperimentEnabled
							? isArchived
							: allowedFeatures.showCopy,
					children: renderCopyLink(),
				},
				{
					name: 'summary',
					shouldShowSeparator: true,
					shouldRender: isWhiteboardSummaryEnabled,
					children: (
						<CreatePageButtonContextProvider
							contentId={contentId}
							spaceKey={contentData?.space?.key}
							contentTitle={contentData?.title}
						>
							<QuickSummaryPopup
								contentId={contentId}
								spaceKey={contentData?.space?.key || undefined}
								contentType={contentType}
								source={contentType}
								buttonStyles={whiteboardSummaryButtonStyles}
								contentPath={window.location.pathname}
							/>
						</CreatePageButtonContextProvider>
					),
				},
				{
					name: 'whiteboard-teammate',
					shouldShowSeparator: true,
					shouldRender: isWhiteboardDigitalTeammateEnabled,
					children: (
						<WhiteboardTeammateButton whiteboardAIAgentService={whiteboardAIAgentService} />
					),
				},
				{
					name: 'share',
					shouldShowSeparator: true,
					shouldRender: !isArchived && !!allowedFeatures.showShare,
					children: renderShare(),
				},
				{
					name: 'moreActions',
					shouldShowSeparator: false,
					shouldRenderInContainer: false,
					shouldRender: !!allowedFeatures.showMoreActions,
					children: (
						<ContentOwnershipContextProvider>
							<Subscribe to={[FlagsStateContainer]}>
								{(flags: FlagsStateContainer) => (
									<MoreActionsComponent
										moreActions={contentActions?.moreActions}
										contentId={contentId}
										contentType={contentType}
										spaceKey={contentData?.space?.key ?? ''}
										allowedFeatures={moreActionsAllowedFeatures}
										hasArchivePermission={hasArchivePermission}
										handleRegisterOnShowHeaderSpotlight={handleRegisterOnShowHeaderSpotlight}
										setShowVersionHistorySpotlight={setShowVersionHistorySpotlight}
										showVersionHistorySpotlight={showVersionHistorySpotlight}
										contentData={contentData}
										flags={flags}
									/>
								)}
							</Subscribe>
						</ContentOwnershipContextProvider>
					),
				},
			];
};
